import type { MailCryptoClient } from '@platform/api-client';
import type { SchedulingInput, SchedulingSuggestions, PlatformRoomList, RoomFinderInput, RoomFinderResult } from '@platform/api-client';
import type { InboxRules, InboxRuleEdit, InboxRuleEditResult } from '@platform/api-client';
/**
 * The webmail talks to the backend ONLY through this interface.
 *
 * - Default: `createRealApi()` against services/platform-api at `VITE_API_BASE_URL`
 *   (deployed: `/webmail/api`).
 * - `VITE_USE_MOCK=1`: in-memory mock mailbox for UI work without a backend (dropped from
 *   production builds because the condition is a build-time constant).
 */
import type { MessageAnalysisView, MessageSourceView, PlatformRuntime, SessionApi } from '@platform/ui';
import type { IdentityClient, GroupwareClient, MfaClient, Draft, DraftInput, DraftSaved, DraftSent } from '@platform/api-client';
import type { CalendarExtraApi } from './calendar-extra';
import type { QuarantineMeApi } from './quarantine';
import type { MyPublicFolderPage, MyPublicFolderQuery } from '@platform/api-client';
import type { ScopedListChange, ScopedListEntries, ScopedLists, TypedList, TypedPasswordWriteInput, TypedListDeleteInput, TypedListSharesInput, ShareGroups } from '@platform/api-client';
import { withVirtualCalendars, type CalendarFeedsApi } from './calendar-feeds';
import { createMockApi } from './mock';
import { createRealApi } from './real';
import type {
  AddressBookFilter,
  BookingRequest,
  MyAttendantSettings,
  MyAttendantSettingsInput,
  AttachedMessage,
  AttachmentLimits,
  CalendarEvent,
  CalendarEventChanges,
  CalendarEventDetail,
  CalendarEventInput,
  CalendarEventScope,
  CalendarEventsResult,
  CalendarInfo,
  CalendarPermissions,
  CalendarPermissionsInput,
  CalendarRespondInput,
  FreeBusy,
  Room,
  UpdateCalendarInput,
  Folder,
  ForwardMessage,
  Identity,
  Message,
  MessagePage,
  AutomaticReplies,
  AutomaticRepliesInput,
  MySignatures,
  PersonalSignatureInput,
  SignatureDefaults,
  OutgoingMessage,
  PeoplePage,
  PersonEntry,
  ReplyMessage,
  ResolveOutcome,
  Session,
  UploadedFile,
  UploadOptions,
  SearchRequest,
  SearchResultPage,
} from './types';

export type * from './types';
export type * from './quarantine';
export { PAGE_SIZE, SOURCE_VIEW_BYTES } from './constants';

export interface WebmailApi extends SessionApi<Session>, CalendarExtraApi, CalendarFeedsApi, QuarantineMeApi {
  groupware: GroupwareClient;
  mailCrypto: MailCryptoClient;
  mfa?: MfaClient;
  identity?: IdentityClient;
  myTypedList(id: string): Promise<TypedList>;
  putMyPasswordListKey(id: string, keyId: string, input: TypedPasswordWriteInput): Promise<TypedList>;
  deleteMyPasswordListKey(id: string, keyId: string, input: TypedListDeleteInput): Promise<TypedList>;
  setMyPasswordListShares(id: string, input: TypedListSharesInput): Promise<TypedList>;
  myShareGroups(): Promise<ShareGroups>;
  myScopedLists(): Promise<ScopedLists>;
  myScopedListEntries(listId: string): Promise<ScopedListEntries>;
  addMyScopedListEntries(listId: string, values: string[]): Promise<ScopedListChange>;
  removeMyScopedListEntries(listId: string, values: string[]): Promise<ScopedListChange>;
  importMyScopedList(listId: string, csv: string): Promise<ScopedListChange>;
  listFolders(): Promise<Folder[]>;
  listPublicFolders(query?: MyPublicFolderQuery): Promise<MyPublicFolderPage>;
  listPublicMessages(folderId: string, offset?: number): Promise<MessagePage>;
  getPublicMessage(folderId: string, itemId: string): Promise<Message>;
  /** Newest first, `PAGE_SIZE` per page. */
  listMessages(folderId: string, offset?: number): Promise<MessagePage>;
  searchMessages(input: SearchRequest): Promise<SearchResultPage>;
  getMessage(id: string): Promise<Message>;
  setRead(id: string, read: boolean): Promise<void>;
  /** Moves the message to Deleted Items. */
  deleteMessage(id: string): Promise<void>;
  send(message: OutgoingMessage): Promise<void>;
  saveDraft(input: DraftInput): Promise<DraftSaved>;
  replaceDraft(id: string, input: DraftInput): Promise<DraftSaved>;
  getDraft(id: string): Promise<Draft>;
  sendDraft(id: string, input: DraftInput): Promise<DraftSent>;
  reply(id: string, message: ReplyMessage): Promise<void>;
  forward(id: string, message: ForwardMessage): Promise<void>;

  // Composer attachments (DESIGN.md T11)
  attachmentLimits(): Promise<AttachmentLimits>;
  /** Uploads one file for the message being composed (progress via options). */
  uploadAttachment(file: File, options?: UploadOptions): Promise<UploadedFile>;
  deleteUpload(id: string): Promise<void>;
  /** `<img src>` of a picture of a message by Content-ID (quoted history); undefined = not shown. */
  inlineImageUrl(messageId: string, contentId: string): string | undefined;
  /** Download link of an attachment (undefined: downloads are "Planned"). */
  attachmentDownloadUrl?: (messageId: string, attachmentId: string) => string;

  /*
   * Optional capabilities: a backend without one shows the matching actions as "Planned"
   * (DESIGN.md §18). The platform API and the mock implement all of them.
   */
  setFlag?: (ids: readonly string[], flagged: boolean) => Promise<void>;
  moveMessages?: (ids: readonly string[], folderId: string) => Promise<void>;
  copyMessages?: (ids: readonly string[], folderId: string) => Promise<void>;
  createFolder?: (parentId: string, name: string) => Promise<void>;
  renameFolder?: (id: string, name: string) => Promise<void>;
  /** Moves the folder with its messages to Deleted Items. */
  deleteFolder?: (id: string) => Promise<void>;
  markAllRead?: (folderId: string) => Promise<void>;
  /** Deletes all messages of the folder permanently (Deleted Items, Junk). */
  emptyFolder?: (folderId: string) => Promise<void>;
  /** Imports a .eml file into the folder; the server scans it before it is stored (progress via options). */
  importMessage?: (folderId: string, file: File, options?: UploadOptions) => Promise<void>;

  /** Public instance data for PlatformProvider (branding, languages); undefined = built-in only. */
  runtime: PlatformRuntime | undefined;

  // Recipients (compose, DESIGN.md T11)
  suggestRecipients(query: string, signal?: AbortSignal): Promise<PersonEntry[]>;
  resolveRecipients(entries: readonly string[]): Promise<ResolveOutcome[]>;
  removeSuggestion(key: string): Promise<void>;
  groupMembers(key: string): Promise<PersonEntry[]>;
  addressBook(query: string, filter: AddressBookFilter, cursor?: string, signal?: AbortSignal): Promise<PeoplePage>;
  /** Own address first, then shared mailboxes the user may send as. */
  identities(): Promise<Identity[]>;
  /** Photo URL of an address (undefined = no photos, e.g. the mock). */
  photoUrl(email: string): string | undefined;

  // Signatures (Settings → Signatures, composer): central ones assigned by the admin + personal ones
  mySignatures(): Promise<MySignatures>;
  /** One personal signature as stored (the list omits templates). */
  mySignature(id: string): Promise<{ template: string }>;
  createSignature(input: PersonalSignatureInput): Promise<MySignatures>;
  updateSignature(id: string, input: PersonalSignatureInput): Promise<MySignatures>;
  deleteSignature(id: string): Promise<MySignatures>;
  /** 403 `signature_locked` when a mandatory signature fixes a default. */
  setSignatureDefaults(defaults: SignatureDefaults): Promise<MySignatures>;

  // Automatic replies (Settings → Automatic replies, banner while active); the mail server holds the state
  myInboxRules(): Promise<InboxRules>;
  saveMyInboxRules(input:InboxRuleEdit):Promise<InboxRuleEditResult>;
  myAutomaticReplies(): Promise<AutomaticReplies>;
  setAutomaticReplies(input: AutomaticRepliesInput): Promise<AutomaticReplies>;

  // Message tools (DESIGN.md T10 message actions)
  /** Raw source for the viewer (first SOURCE_VIEW_BYTES). */
  getSource(id: string): Promise<MessageSourceView>;
  /** Link that downloads the complete .eml. */
  sourceDownloadUrl(id: string): string;
  /** Header analysis for "Message details". */
  getAnalysis(id: string): Promise<MessageAnalysisView>;
  getAttachedMessage(id: string, attachmentId: string): Promise<AttachedMessage>;

  // Calendar (DESIGN.md T13): the server applies the rights gate and masks private items of others.
  /** Own calendar first, then the calendars the user added (with effective access). */
  listCalendars(): Promise<CalendarInfo[]>;
  addCalendar(address: string, kind?: 'shared' | 'room' | 'equipment'): Promise<CalendarInfo>;
  /** A new calendar folder in the own mailbox (Outlook and phones see it too). */
  createOwnCalendar(name: string): Promise<CalendarInfo>;
  updateCalendar(id: string, changes: UpdateCalendarInput): Promise<CalendarInfo>;
  /** Added calendars leave the list; additional own calendars are deleted (to Deleted Items). */
  removeCalendar(id: string): Promise<void>;
  /** Events of several calendars in [start, end) (ISO), one result (or error) per calendar. */
  /** `zone`: IANA zone for the all-day dates of read-only calendars (default: the browser's). */
  listEvents(calendarIds: readonly string[], start: string, end: string, zone?: string): Promise<CalendarEventsResult[]>;
  getEvent(id: string): Promise<CalendarEventDetail>;
  createEvent(calendarId: string, input: CalendarEventInput): Promise<CalendarEvent>;
  /** `notify: false`: the organizer saves without sending an update to the attendees. */
  updateEvent(id: string, changes: CalendarEventChanges, scope?: CalendarEventScope, notify?: boolean): Promise<CalendarEvent>;
  /** Organizer: cancels the meeting; attendee: declines (notify) and removes it. */
  deleteEvent(id: string, options?: { scope?: CalendarEventScope; notify?: boolean; comment?: string }): Promise<void>;
  /** Accept/tentative/decline (calendar item or meeting request id); returns the calendar item id. */
  respondToEvent(id: string, input: CalendarRespondInput): Promise<string | null>;
  freeBusy(attendees: readonly string[], start: string, end: string): Promise<FreeBusy>;
  schedulingSuggestions(input: SchedulingInput): Promise<SchedulingSuggestions>;
  roomLists(): Promise<{ lists: PlatformRoomList[] }>;
  findRooms(input: RoomFinderInput): Promise<RoomFinderResult>;
  listRooms(): Promise<Room[]>;
  /** Sharing of an own calendar (`own` or an additional one). */
  calendarPermissions(calendarId: string): Promise<CalendarPermissions>;
  setCalendarPermissions(calendarId: string, input: CalendarPermissionsInput): Promise<CalendarPermissions>;
  // Calendar attendant: own invitation settings and booking requests of resources the user approves (delegate).
  myAttendantSettings(): Promise<MyAttendantSettings>;
  setMyAttendantSettings(input: MyAttendantSettingsInput): Promise<MyAttendantSettings>;
  bookingRequests(): Promise<BookingRequest[]>;
  decideBookingRequest(id: string, verdict: 'approve' | 'decline', comment?: string): Promise<BookingRequest>;
}

export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '/api';
/** Absolute URL of a server path the API reports as "/api/…" (e.g. a published calendar): served below this app's API base. */
export function apiPublicUrl(path: string, base: string = API_BASE_URL, origin: string = window.location.origin): string {
  return new URL(`${base.replace(/\/$/, '')}${path.replace(/^\/api(?=\/)/, '')}`, origin).toString();
}
export const USE_MOCK: boolean = import.meta.env.VITE_USE_MOCK === '1';

export const api: WebmailApi = withVirtualCalendars(USE_MOCK ? createMockApi() : createRealApi(API_BASE_URL));
